import { Helmet } from 'react-helmet-async';

/**
 * WebsiteSchema - Comprehensive Schema.org structured data for the entire website
 * Includes Organization, WebSite, and Service schemas
 */
const WebsiteSchema: React.FC = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://puntohispano.ch/#organization",
    "name": "Punto Hispano",
    "alternateName": ["Punto Hispano Holding", "Punto Hispano Suiza"],
    "legalName": "Punto Hispano Holding",
    "url": "https://puntohispano.ch",
    "logo": {
      "@type": "ImageObject",
      "url": "https://lhfezfmidegmuwkdwjzt.supabase.co/storage/v1/object/public/img/LOGOS/Logo%20V1%20-%20Punto%20hispano",
      "width": 512,
      "height": 512
    },
    "image": "https://lhfezfmidegmuwkdwjzt.supabase.co/storage/v1/object/public/img/LOGOS/Logo%20V1%20-%20Punto%20hispano",
    "description": "Servicios profesionales integrales para hispanohablantes en Suiza. Gestoría, asesoría legal, seguros, idiomas y soluciones digitales.",
    "foundingDate": "2015",
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "minValue": 40,
      "maxValue": 50
    },
    "slogan": "Tu conexión con Suiza",
    "telephone": "+41772317288",
    "email": "info@puntohispano.ch",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Thurgauerstrasse 117",
      "addressLocality": "Opfikon",
      "addressRegion": "Zürich",
      "postalCode": "8152",
      "addressCountry": "CH"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "47.4245",
      "longitude": "8.5692"
    },
    "areaServed": [
      {
        "@type": "Country",
        "name": "Switzerland",
        "sameAs": "https://en.wikipedia.org/wiki/Switzerland"
      }
    ],
    "knowsLanguage": ["Spanish", "German", "English", "French", "Italian", "Portuguese"],
    "sameAs": [
      "https://www.facebook.com/p/Punto-Hispano-61553177270040/",
      "https://www.instagram.com/puntohispanoch/",
      "https://www.tiktok.com/@puntohispano.ch",
      "https://www.linkedin.com/company/punto-hispano"
    ],
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+41772317288",
        "contactType": "customer service",
        "availableLanguage": ["Spanish", "German", "English"],
        "areaServed": "CH"
      },
      {
        "@type": "ContactPoint",
        "email": "info@puntohispano.ch",
        "contactType": "sales",
        "availableLanguage": ["Spanish", "German", "English"]
      }
    ]
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://puntohispano.ch/#website",
    "url": "https://puntohispano.ch",
    "name": "Punto Hispano",
    "description": "Servicios profesionales para hispanohablantes en Suiza",
    "publisher": {
      "@id": "https://puntohispano.ch/#organization"
    },
    "inLanguage": ["es", "de", "en"],
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://puntohispano.ch/buscar?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Servicios de Punto Hispano",
    "description": "Lista completa de servicios para hispanohablantes en Suiza",
    "numberOfItems": 8,
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Service",
          "name": "Gestoría y Administración",
          "description": "RAV, permisos, documentación, autónomos y empresas, contabilidad",
          "url": "https://puntohispano.ch/servicios/gestoria-administracion",
          "provider": { "@id": "https://puntohispano.ch/#organization" },
          "areaServed": { "@type": "Country", "name": "Switzerland" },
          "serviceType": "Administrative Services"
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Service",
          "name": "Asesoría Legal",
          "description": "Derecho laboral, migratorio, fiscal e impuestos",
          "url": "https://puntohispano.ch/servicios/asesoria-legal",
          "provider": { "@id": "https://puntohispano.ch/#organization" },
          "areaServed": { "@type": "Country", "name": "Switzerland" },
          "serviceType": "Legal Services"
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "Service",
          "name": "Cursos de Idiomas",
          "description": "Cursos de alemán e inglés para hispanohablantes",
          "url": "https://puntohispano.ch/servicios/cursos-idiomas",
          "provider": { "@id": "https://puntohispano.ch/#organization" },
          "areaServed": { "@type": "Country", "name": "Switzerland" },
          "serviceType": "Education"
        }
      },
      {
        "@type": "ListItem",
        "position": 4,
        "item": {
          "@type": "Service",
          "name": "Consultoría de Seguros",
          "description": "Seguros obligatorios, tercer pilar, pensiones",
          "url": "https://puntohispano.ch/servicios/consultoria-seguros",
          "provider": { "@id": "https://puntohispano.ch/#organization" },
          "areaServed": { "@type": "Country", "name": "Switzerland" },
          "serviceType": "Insurance"
        }
      },
      {
        "@type": "ListItem",
        "position": 5,
        "item": {
          "@type": "Service",
          "name": "Soluciones Digitales",
          "description": "Desarrollo web, SEO, automatización, IA",
          "url": "https://puntohispano.ch/servicios/soluciones-digitales-avanzadas",
          "provider": { "@id": "https://puntohispano.ch/#organization" },
          "areaServed": { "@type": "Country", "name": "Switzerland" },
          "serviceType": "Digital Services"
        }
      },
      {
        "@type": "ListItem",
        "position": 6,
        "item": {
          "@type": "Service",
          "name": "Alquiler de Vehículos",
          "description": "Alquiler de coches flexible en Suiza",
          "url": "https://puntohispano.ch/servicios/alquileres-vehiculos",
          "provider": { "@id": "https://puntohispano.ch/#organization" },
          "areaServed": { "@type": "Country", "name": "Switzerland" },
          "serviceType": "Vehicle Rental"
        }
      },
      {
        "@type": "ListItem",
        "position": 7,
        "item": {
          "@type": "Service",
          "name": "Alquiler de Viviendas",
          "description": "Habitaciones y apartamentos en alquiler",
          "url": "https://puntohispano.ch/servicios/alquileres-viviendas",
          "provider": { "@id": "https://puntohispano.ch/#organization" },
          "areaServed": { "@type": "Country", "name": "Switzerland" },
          "serviceType": "Housing Rental"
        }
      },
      {
        "@type": "ListItem",
        "position": 8,
        "item": {
          "@type": "Service",
          "name": "Pack Relocation",
          "description": "Servicio integral de mudanza y asentamiento",
          "url": "https://puntohispano.ch/servicios/pack-relocation",
          "provider": { "@id": "https://puntohispano.ch/#organization" },
          "areaServed": { "@type": "Country", "name": "Switzerland" },
          "serviceType": "Relocation Services"
        }
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(servicesSchema)}
      </script>
    </Helmet>
  );
};

export default WebsiteSchema;